import { initializeApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  NextOrObserver,
  User,
  OAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
  sendPasswordResetEmail,
} from "firebase/auth";
import { getFirebaseConfig } from "./firebase-config";
import { collection, doc, getDoc, DocumentData } from "firebase/firestore";
import log from "../utils/logger";

const app = initializeApp(getFirebaseConfig());
const auth = getAuth(app);

export const DASHBOARD_ROLES = ["SuperAdmin", "TenantAdmin", "RestaurantUser"];

export const canUseDashboard = (user?: { role?: string } | null) => !!user && DASHBOARD_ROLES.includes(user.role ?? "");

export const NO_DASHBOARD_ACCESS = "This account doesn't have access to the admin dashboard. If you think it should, please contact support@ddilabs.ai.";

export type StoredUser = DocumentData & {
  uid: string;
  email: string | null;
  providerData: { providerId: string; uid: string; email: string | null; displayName: string | null }[];
  role?: string;
};

// What the dashboard keeps of the signed-in person: their account record, never Firebase's tokens.
export const toStoredUser = (user: User, dbUser: DocumentData | undefined): StoredUser => ({
  uid: user.uid,
  email: user.email,
  providerData: user.providerData.map(({ providerId, uid, email, displayName }) => ({ providerId, uid, email, displayName })),
  ...dbUser,
});

// One read of the person's account per sign-in, shared by the sign-in page and the route guard.
let sessionRead: { uid: string; read: Promise<StoredUser | null> } | null = null;

export const readSessionUser = (user: User) => {
  if (sessionRead?.uid !== user.uid) {
    const read = getFirestoreUser(user.uid).then((dbUser) => {
      // Signed out, or someone else signed in, while the account was being read.
      if (auth.currentUser?.uid !== user.uid) return null;
      const stored = toStoredUser(user, dbUser);
      localStorage.setItem("user", JSON.stringify(stored));
      return stored;
    });
    read.catch(() => {
      if (sessionRead?.read === read) sessionRead = null;
    });
    sessionRead = { uid: user.uid, read };
  }
  return sessionRead.read;
};

export const forgetSessionUser = () => {
  sessionRead = null;
  localStorage.removeItem("user");
};

const TRY_AGAIN_LATER = "Something went wrong on our side. Please try again in a moment.";
const OFFLINE = "We couldn't reach the server. Please check your internet connection and try again.";

export type SignInField = "email" | "password";

export const signInError = (code: string): { error: string; field: SignInField } => {
  switch (code) {
    case "auth/invalid-email":
      return { error: "That doesn't look like an email address. Please check it and try again.", field: "email" };
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
      return { error: "That email and password don't match. Please try again, or reset your password if you've forgotten it.", field: "password" };
    case "auth/user-disabled":
      return { error: "This account has been switched off. Please contact support@ddilabs.ai if you need access again.", field: "email" };
    case "auth/too-many-requests":
      return { error: "That's a lot of attempts in a row, so we've paused sign-in for a few minutes. Please wait and try again, or reset your password.", field: "password" };
    case "auth/network-request-failed":
      return { error: OFFLINE, field: "password" };
    default:
      return { error: TRY_AGAIN_LATER, field: "password" };
  }
};

export const signInUser = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const stored = await readSessionUser(userCredential.user);
    if (!canUseDashboard(stored)) {
      await SignOutUser();
      return { error: NO_DASHBOARD_ACCESS, field: "email" as SignInField };
    }
    log({collection: "users", documentId: userCredential.user.uid, message: "User signed in", type: "read"});
    return { user: stored, error: null };
  } catch (error) {
    // Signed in to Firebase but the account couldn't be read: don't leave a half-finished session.
    if (auth.currentUser) await SignOutUser().catch(() => undefined);
    return signInError((error as { code?: string }).code ?? "");
  }
};

export const userStateListener = (callback: NextOrObserver<User>) => {
  return onAuthStateChanged(auth, callback);
};

export const SignOutUser = async () => {
  forgetSessionUser();
  await signOut(auth);
};

// Microsoft Auth
const microsoftProvider = new OAuthProvider("microsoft.com");

// Opens Microsoft's sign-in straight away (a lookup first would need reading other people's
// accounts while signed out, which the rules refuse), then checks who it turned out to be.
export const signInWithMicrosoft = async (email: string): Promise<{ user?: StoredUser | null; error: string | null }> => {
  // Target specific email with login hint, when one was typed.
  microsoftProvider.setCustomParameters(email ? { login_hint: email } : {});
  try {
    const userCredential = await signInWithPopup(auth, microsoftProvider);
    const stored = await readSessionUser(userCredential.user);
    if (!canUseDashboard(stored)) {
      // A first Microsoft sign-in creates a Firebase account. Don't keep one for someone who has
      // no account with us, or it would block inviting that email later.
      if (getAdditionalUserInfo(userCredential)?.isNewUser && !stored?.role) {
        await userCredential.user.delete().catch(() => undefined);
      }
      await SignOutUser();
      return { error: NO_DASHBOARD_ACCESS };
    }
    log({collection: "users", documentId: userCredential.user.uid, message: "User signed in", type: "read"});
    return { user: stored, error: null };
  } catch (error) {
    if (auth.currentUser) await SignOutUser().catch(() => undefined);
    switch ((error as { code?: string }).code) {
      // They closed Microsoft's window themselves; nothing to explain.
      case "auth/popup-closed-by-user":
      case "auth/cancelled-popup-request":
      case "auth/user-cancelled":
        return { error: null };
      case "auth/popup-blocked":
        return { error: "Your browser blocked the Microsoft sign-in window. Please allow pop-ups for this site and try again." };
      case "auth/account-exists-with-different-credential":
        return { error: "This email signs in with a password rather than Microsoft. Please go back and use Sign In with Email and Password." };
      case "auth/network-request-failed":
        return { error: OFFLINE };
      default:
        return { error: TRY_AGAIN_LATER };
    }
  }
};

export const getFirestoreUser = async (uid: string) => {
  const db = await import("firebase/firestore");
  const firestore = db.getFirestore();
  const userRef = collection(firestore, "users");
  const userSnap = await getDoc(doc(userRef, uid));
  return userSnap.data();
};

export const updateUserName = async (uid: string, name: string) => {
  const db = await import("firebase/firestore"); // Lazy load Firestore because this function is not always used
  const firestore = db.getFirestore(app);
  const userRef = db.doc(firestore, "users", uid);
  await db.updateDoc(userRef, { name });
  return { uid, name };
};

export const sendPasswordResetEmailToUser = async (email: string): Promise<{ error: string | null }> => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { error: null };
  } catch (error) {
    switch ((error as { code?: string }).code) {
      // Answer as if it was sent, so this can't be used to find out who has an account.
      case "auth/user-not-found":
        return { error: null };
      case "auth/invalid-email":
        return { error: "That doesn't look like an email address. Please check it and try again." };
      case "auth/too-many-requests":
        return { error: "We've sent a few reset links already. Please wait a few minutes before asking for another." };
      case "auth/network-request-failed":
        return { error: OFFLINE };
      default:
        return { error: TRY_AGAIN_LATER };
    }
  }
};
